import { gql } from "@apollo/client";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  makeStyles
} from "@material-ui/core";

import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import {
  campaignEditCallAttempts,
  campaignEditCallAttemptsVariables
} from "src/graphql/generated/campaignEditCallAttempts";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { CampaignStatus } from "src/graphql/generated/globalTypes";
import { useFormInputStyles } from "src/styles/forms";
import useMutation from "src/util/useMutation";

import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import { FormikSaveButtons } from "./SaveButtons";

const MAX_PASSES = 5;

const useStyles = makeStyles(theme => ({
  explainer: {
    marginBottom: theme.spacing(3)
  },
  header: {
    margin: `${theme.spacing(3)}px 0 0`
  },
  noCallWindowLabel: {
    marginBottom: theme.spacing(1)
  },

  noCallWindowInput: {
    "& label": {
      fontWeight: "normal"
    },
    width: "10em"
  },
  minutes: {
    marginLeft: theme.spacing(2)
  }
}));

type Values = {
  maxCallAttempts: string;
  noCallWindowHours: string;
  noCallWindowMinutes: string;
  answeringMachineDropCallAttempt: string;
  goToNext?: boolean;
};

const schema = yup.object().shape({
  maxCallAttempts: yup.string().required("Required"),
  noCallWindowHours: yup.string().trim(),
  noCallWindowMinutes: yup
    .string()
    .trim()
    .test(
      "in-minutes",
      "Must be between 0 and 59 minutes",
      value => !!value && parseInt(value, 10) < 60 && parseInt(value, 10) >= 0
    )
    .test(
      "more-than-20-minutes",
      "The minimum time between passes is 20 minutes",
      (value, { parent }) =>
        (!!parent.noCallWindowHours &&
          parseInt(parent.noCallWindowHours, 10) > 0) ||
        (!!value && parseInt(value, 10) >= 20)
    )
    .required("Required"),
  answeringMachineDropCallAttempt: yup.string().required("Required")
});

const EDIT_CALL_ATTEMPTS = gql`
  mutation campaignEditCallAttempts(
    $id: ID!
    $maxCallAttempts: Int!
    $noCallWindow: Int!
    $answeringMachineDropCallAttempt: Int!
  ) {
    campaignEditCallAttempts(
      id: $id
      callAttempts: {
        maxCallAttempts: $maxCallAttempts
        noCallWindow: $noCallWindow
        answeringMachineDropCallAttempt: $answeringMachineDropCallAttempt
      }
    ) {
      id
      max_call_attempts
      no_call_window
      answering_machine_drop_call_attempt
    }
  }
`;

export default function MultiplePasses({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const formInputStyles = useFormInputStyles();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const [editCallAttempts] = useMutation<
    campaignEditCallAttempts,
    campaignEditCallAttemptsVariables
  >(EDIT_CALL_ATTEMPTS, {
    onErrorSnackbarMessage: "Could not edit campaign"
  });

  const sendEditEvent = useCampaignEditEvents();

  const extractNoCallWindowHours = (noCallWindow: number) =>
    (Math.floor(noCallWindow / 60) || "").toString();
  const extractNoCallWindowMinutes = (noCallWindow: number) =>
    _.pad((noCallWindow % 60).toString(), 2, "0");

  const handleSubmit = async (
    submittedValues: Values,
    formikHelpers: FormikHelpers<Values>
  ) => {
    const maxCallAttempts = parseInt(submittedValues.maxCallAttempts, 10);
    const maxCallAttemptsIsIncreasing =
      maxCallAttempts > campaign.max_call_attempts;
    const campaignIsRunning = campaign.status !== CampaignStatus.DRAFT;
    if (maxCallAttemptsIsIncreasing && campaignIsRunning) {
      setConfirmationDialogOpen(true);
    } else {
      await handleSave(submittedValues, formikHelpers);
    }
  };

  const handleCancelConfirmation = async ({
    setFieldValue
  }: FormikHelpers<Values>) => {
    setConfirmationDialogOpen(false);
    setFieldValue("maxCallAttempts", campaign.max_call_attempts?.toString());
  };

  const handleConfirmation = async (props: FormikProps<Values>) => {
    setConfirmationDialogOpen(false);
    await handleSave(props.values, props);
  };

  const handleSave = async (
    {
      maxCallAttempts,
      noCallWindowHours,
      noCallWindowMinutes,
      answeringMachineDropCallAttempt,
      goToNext
    }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    const { errors, data } = await editCallAttempts({
      variables: {
        id: campaign.id,
        maxCallAttempts: parseInt(maxCallAttempts, 10),
        noCallWindow:
          (parseInt(noCallWindowHours, 10) || 0) * 60 +
          parseInt(noCallWindowMinutes, 10),
        answeringMachineDropCallAttempt: parseInt(
          answeringMachineDropCallAttempt,
          10
        )
      }
    });
    setSubmitting(false);
    if (!errors) {
      sendEditEvent();
      resetForm({
        values: {
          maxCallAttempts:
            data!.campaignEditCallAttempts?.max_call_attempts?.toString(),
          noCallWindowHours: extractNoCallWindowHours(
            data!.campaignEditCallAttempts?.no_call_window
          ),
          noCallWindowMinutes: extractNoCallWindowMinutes(
            data!.campaignEditCallAttempts?.no_call_window
          ),
          answeringMachineDropCallAttempt:
            data!.campaignEditCallAttempts?.answering_machine_drop_call_attempt?.toString()
        }
      });
      if (goToNext) {
        goToNextSection();
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  return (
    <Formik
      initialValues={{
        maxCallAttempts: campaign.max_call_attempts.toString(),
        noCallWindowHours: extractNoCallWindowHours(campaign.no_call_window),
        noCallWindowMinutes: extractNoCallWindowMinutes(
          campaign.no_call_window
        ),
        answeringMachineDropCallAttempt:
          campaign.answering_machine_drop_call_attempt.toString()
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {props => (
        <Form>
          <RouteLeavingGuard when={props.dirty} />
          <h2>Number of Passes</h2>
          <FormikErrorBanner />
          <div className={classes.explainer}>
            Choose the number of times you wish to call contacts in this
            campaign. The <em>Disposition</em> settings in the first question of
            your script determines which contacts the dialer will try to call
            again.{" "}
            <a
              target="_BLANK"
              href="https://scaletowin.freshdesk.com/support/solutions/articles/66000506178-step-7-number-of-passes"
            >
              Learn more in our guide
            </a>
            .
          </div>
          <FormikSelect
            label="Number of Passes"
            name="maxCallAttempts"
            fullWidth
          >
            {_.times(MAX_PASSES, attempt => (
              <MenuItem value={`${attempt + 1}`}>{attempt + 1}</MenuItem>
            ))}
          </FormikSelect>
          <InputLabel
            shrink
            className={`${formInputStyles.inputLabel} ${classes.noCallWindowLabel}`}
          >
            Minimum Time Required Between Passes
          </InputLabel>
          <FormikTextField
            label="Hours"
            name="noCallWindowHours"
            className={classes.noCallWindowInput}
            size="small"
          />
          <FormikTextField
            label="Minutes"
            name="noCallWindowMinutes"
            className={`${classes.noCallWindowInput} ${classes.minutes}`}
            size="small"
          />
          <FormikSelect
            label="Pass to Leave Voicemail During"
            name="answeringMachineDropCallAttempt"
            disabled={!campaign.answering_machine_drop}
            fullWidth
          >
            {_.times(parseInt(props.values.maxCallAttempts, 10), attempt => (
              <MenuItem value={`${attempt + 1}`}>{attempt + 1}</MenuItem>
            ))}
          </FormikSelect>
          <FormikSaveButtons continueWithoutSaving={goToNextSection} />
          <Dialog open={confirmationDialogOpen} fullWidth maxWidth="md">
            <DialogTitle>New Pass</DialogTitle>
            <DialogContent>
              <div>
                This new pass has now been added to your already-running
                campaign. Note that the “Minimum Time Required Between Passes”
                that you specified must elapse before your contacts will be
                dialed again.
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleCancelConfirmation(props)}>
                Cancel New Pass
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                onClick={() => handleConfirmation(props)}
              >
                Continue
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
}
